import React, { useState, useEffect } from 'react';
import axios from 'axios';

/** Generic min/max bet + RTP + active toggle panel, used for Mines and Limbo admin settings.
 *  Blackjack keeps its own richer panel (logo/card-back uploads) in BlackjackSettings.js. */
const GameSettingsPanel = ({ gameType, title, defaults, showMines = false, showMaxWin = false }) => {
  const [settings, setSettings] = useState({
    minBet: defaults.minBet,
    maxBet: defaults.maxBet,
    rtp: defaults.rtp,
    isActive: true,
    minMines: defaults.minMines || 1,
    maxMines: defaults.maxMines || 24,
    maxWin: defaults.maxWin ?? ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/games/settings/${gameType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      setSettings({
        minBet: d.minBet || d.min_bet || defaults.minBet,
        maxBet: d.maxBet || d.max_bet || defaults.maxBet,
        rtp: d.rtp ?? defaults.rtp,
        isActive: d.isActive !== undefined ? d.isActive : (d.is_active === 1 || d.is_active === true),
        minMines: d.minMines || defaults.minMines || 1,
        maxMines: d.maxMines || defaults.maxMines || 24,
        maxWin: (d.maxWin ?? d.max_win) ?? defaults.maxWin ?? ''
      });
    } catch (error) {
      // No row saved yet — defaults are already applied from props.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('minBet', settings.minBet);
      formData.append('maxBet', settings.maxBet);
      formData.append('rtp', settings.rtp);
      formData.append('isActive', settings.isActive);
      if (showMines) {
        formData.append('minMines', settings.minMines);
        formData.append('maxMines', settings.maxMines);
      }
      if (showMaxWin) {
        formData.append('maxWin', settings.maxWin === '' ? '' : parseInt(settings.maxWin) || 0);
      }
      await axios.put(`/api/games/settings/${gameType}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (error) {
      alert(error.response?.data?.error || 'Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20 max-w-xl">
      <h3 className="text-xl font-bold text-gold mb-4">{title}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/70 mb-1">Min Bahis</label>
            <input
              type="number"
              value={settings.minBet}
              onChange={(e) => setSettings({ ...settings, minBet: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Maks Bahis</label>
            <input
              type="number"
              value={settings.maxBet}
              onChange={(e) => setSettings({ ...settings, maxBet: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
            />
          </div>
        </div>

        {showMines && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/70 mb-1">Min Mayın</label>
              <input
                type="number"
                min={1}
                max={24}
                value={settings.minMines}
                onChange={(e) => setSettings({ ...settings, minMines: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1">Maks Mayın</label>
              <input
                type="number"
                min={1}
                max={24}
                value={settings.maxMines}
                onChange={(e) => setSettings({ ...settings, maxMines: parseInt(e.target.value) || 24 })}
                className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm text-white/70 mb-1">
            RTP (Oyuncuya Dönüş Oranı — {Math.round(settings.rtp * 100)}%, kasa avantajı %{Math.round((1 - settings.rtp) * 100 * 10) / 10})
          </label>
          <input
            type="number"
            step="0.001"
            min="0.5"
            max="0.999"
            value={settings.rtp}
            onChange={(e) => setSettings({ ...settings, rtp: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
          />
        </div>

        {showMaxWin && (
          <div>
            <label className="block text-sm text-white/70 mb-1">
              Maksimum Kazanç (tek elde — çarpan ne olursa olsun tavan, boş = sınırsız)
            </label>
            <input
              type="number"
              min="0"
              placeholder="Örn: 20000"
              value={settings.maxWin}
              onChange={(e) => setSettings({ ...settings, maxWin: e.target.value })}
              className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white focus:outline-none focus:border-gold"
            />
          </div>
        )}

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.isActive}
            onChange={(e) => setSettings({ ...settings, isActive: e.target.checked })}
            className="w-5 h-5 accent-gold"
          />
          <span className="text-white">Oyun aktif (Saguex Originals'da görünür)</span>
        </label>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-gradient-to-r from-gold to-yellow-500 text-navy-900 font-bold px-6 py-2.5 rounded-lg transition-all disabled:opacity-50"
          >
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          {saved && <span className="text-green-400 text-sm font-semibold">✓ Kaydedildi</span>}
        </div>
      </form>
    </div>
  );
};

export default GameSettingsPanel;
